import { getRedirectUrl, type PartnerType } from "../../generated-api/account";
import { createClient, createConfig } from "../../generated-api/account/client";
import {
  Configuration,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";

let port = "";
if (
  window.location.port &&
  window.location.port !== "" &&
  window.location.port !== "80"
) {
  port = `:${window.location.port}`;
}
const BASE_URL = `${window.location.protocol}//${window.location.hostname}${port}`;
const LINKING_CALLBACK_PATH = "/auth/linking-callback";

const linkingPartner =
  (import.meta.env.VITE_LINKING_PARTNER as PartnerType | undefined) ||
  "Wibutler";

export const isLinkingAuthConfigured =
  (import.meta.env.VITE_API_BASE_ACCOUNT_URL || "") !== "";

export const oidcSettings: Configuration = {
  auth: {
    authority: import.meta.env.VITE_AUTHORITY,
    clientId: import.meta.env.VITE_CLIENT_ID,
    OIDCOptions: {
      defaultScopes: [...import.meta.env.VITE_SCOPE.split(" ")],
    },
    knownAuthorities: [import.meta.env.VITE_AUTHORITY],
    protocolMode: "OIDC",
    postLogoutRedirectUri: BASE_URL,
    redirectUri: BASE_URL,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    allowNativeBroker: false,
  },
};

const coreInstance = new PublicClientApplication(oidcSettings);
export const alertInstance = new PublicClientApplication({
  auth: {
    authority: import.meta.env.VITE_AUTHORITY,
    clientId: import.meta.env.VITE_CLIENT_ID,
    OIDCOptions: {
      defaultScopes: ["openid", "profile"],
    },
    knownAuthorities: [import.meta.env.VITE_AUTHORITY],
    protocolMode: "OIDC",
    postLogoutRedirectUri: BASE_URL,
    redirectUri: BASE_URL,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    allowNativeBroker: false,
  },
});

const subscriptionInstance = new PublicClientApplication({
  auth: {
    authority: import.meta.env.VITE_AUTHORITY,
    clientId: import.meta.env.VITE_CLIENT_ID,
    OIDCOptions: {
      defaultScopes: ["openid", "profile"],
    },
    knownAuthorities: [import.meta.env.VITE_AUTHORITY],
    protocolMode: "OIDC",
    postLogoutRedirectUri: BASE_URL,
    redirectUri: BASE_URL,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    allowNativeBroker: false,
  },
});

const accountLinkingClient = createClient(
  createConfig({
    baseUrl: import.meta.env.VITE_API_BASE_ACCOUNT_URL,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    auth: () => acquireCoreToken(deviceManagerScopes),
  }),
);

let initializationPromise: Promise<void> | null = null;

export const msalInstance = coreInstance;

export const initializeAuthInstances = async () => {
  if (!initializationPromise) {
    initializationPromise = Promise.all([
      coreInstance.initialize(),
      alertInstance.initialize(),
      subscriptionInstance.initialize(),
    ]).then(() => undefined);
  }

  await initializationPromise;
};

export const deviceManagerScopes = import.meta.env.VITE_SCOPE
  ? [...import.meta.env.VITE_SCOPE.split(" ")]
  : [];

export const acquireCoreToken = async (scopes: string[]) => {
  return await acquireToken(scopes, coreInstance);
};

export const acquireAlertToken = async (scopes: string[]) => {
  return await acquireToken(scopes, alertInstance);
};

export const acquireSubscriptionToken = async (scopes: string[]) => {
  return await acquireToken(scopes, subscriptionInstance);
};

export const startLinkAccountRedirect = async () => {
  if (!isLinkingAuthConfigured) {
    console.error(
      "[AccountLinking] Missing VITE_API_BASE_ACCOUNT_URL. Cannot request redirect URL.",
    );
    return;
  }

  await initializeAuthInstances();

  const callbackUrl = `${BASE_URL}${LINKING_CALLBACK_PATH}`;
  const state = `${window.location.pathname}${window.location.search}`;

  const { data } = await getRedirectUrl({
    client: accountLinkingClient,
    throwOnError: true,
    path: {
      partner: linkingPartner,
    },
    query: {
      callbackUrl,
      state,
    },
  });

  const redirectUrl = data?.model?.redirectUrl;

  if (!redirectUrl) {
    console.error(
      "[AccountLinking] AccountAPI returned no redirect URL for linking.",
    );
    return;
  }

  window.location.assign(redirectUrl);
};

export const handleLinkingRedirectCallback = async () => {
  if (window.location.pathname !== LINKING_CALLBACK_PATH) {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");

  const data = localStorage.getItem("@gf-core/widgets");
  const parsedData = data ? JSON.parse(data) : null;
  const rules = parsedData ? parsedData.state.wibutlerRules : {};
  const rule = state && rules ? rules[state] : null;
  const redirectUrl = rule?.href;

  if (redirectUrl) {
    const url = new URL(redirectUrl);
    if (code) {
      url.searchParams.set("code", code);
    }

    if (state) {
      url.searchParams.set("state", state);
    }

    const safeRedirectUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, document.title, safeRedirectUrl);
    return;
  }
};

const acquireToken = async (
  scopes: string[],
  instance: PublicClientApplication,
) => {
  await initializeAuthInstances();

  try {
    // Use blank redirectUri: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/errors.md#block_iframe_reload
    const result = await instance.acquireTokenSilent({
      scopes,
      redirectUri: `${BASE_URL}/blank.html`,
    });
    return result.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      // When session at IDP expires, it will throw InteractionRequiredAuthError. In this case, we need to redirect user to login page to acquire new token.
      await instance.acquireTokenRedirect({ scopes });
    }
    throw e;
  }
};
